import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { RicsLabelMaps } from '../../selection-lists/hooks/useRicsTaxonomyLabels';

/**
 * Resolve a UI taxonomy id to its label. k50 ids (1-50) map straight to the k50
 * label; k10 ids (51-60) map to `<id> - <label>` (k10 raw ids are offset by +50
 * for the UI). Anything outside those ranges — 0, custom taxonomies, unknown —
 * is the default.
 */
function getRoleTaxonomyLabel(
  labels: RicsLabelMaps | null | undefined,
  uiId?: number
): string {
  if (!uiId) return 'Default';
  if (uiId >= 1 && uiId <= 50) {
    return labels?.rics_k50.get(uiId) ?? 'Default';
  }
  if (uiId >= 51 && uiId <= 60) {
    const name = labels?.rics_k10.get(uiId - 50);
    return name ? `${uiId} - ${name}` : 'Default';
  }
  return 'Default';
}

export function resolveAdaptiveLabelFromSelection(
  labels: RicsLabelMaps | null | undefined,
  selection?: CustomRoleTaxonomySelection | null
) {
  if (!selection) return undefined;
  if (selection.default) return 'Default';
  let uiId: number | undefined;
  if (selection.ricsK50Id) uiId = parseInt(selection.ricsK50Id, 10);
  else if (selection.ricsK10Id) uiId = parseInt(selection.ricsK10Id, 10) + 50;
  if (!uiId) return undefined;
  return getRoleTaxonomyLabel(labels, uiId);
}

export function resolveAdaptiveLabelFromId(
  labels: RicsLabelMaps | null | undefined,
  selection?: number | null
) {
  if (selection === null || typeof selection === 'undefined') return undefined;
  if (selection === 0) return 'Default';
  if (selection >= 1 && selection <= 60) {
    return getRoleTaxonomyLabel(labels, selection);
  }
  return undefined;
}

/**
 * Returns a standardized display name for known taxonomy sources.
 * - onet -> ONET
 * - esco -> ESCO
 * - lightcast -> Lightcast
 */
export function getPrettyTaxonomyName(rawName?: string) {
  const name = (rawName || '').trim();
  if (!name) return 'Custom';
  const lower = name.toLowerCase();
  if (lower === 'onet') return 'ONET';
  if (lower === 'esco') return 'ESCO';
  if (lower === 'lightcast') return 'Lightcast';
  return name.charAt(0).toUpperCase() + name.slice(1);
}
